import React, { useEffect, useState } from 'react';
import {
  getOneSignalAppStatus,
  requestOneSignalPushPermission,
} from '../../utils/onesignal';

const NEWS_PUSH_PROMPT_DISMISSED_KEY = 'pfpNewsPushPromptDismissedAt';

function formatDate(value) {
  if (!value) {
    return 'ohne Datum';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'ohne Datum';
  }

  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTypeLabel(type) {
  switch (type) {
    case 'critical':
      return 'Kritisch';
    case 'important':
      return 'Wichtig';
    default:
      return 'Info';
  }
}

function shouldShowPromptAgain() {
  if (typeof window === 'undefined') {
    return true;
  }

  const dismissedAt = Number(window.localStorage?.getItem(NEWS_PUSH_PROMPT_DISMISSED_KEY) || 0);
  if (!dismissedAt) {
    return true;
  }

  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt > sevenDays;
}

function NewsPushOptInCard() {
  const [status, setStatus] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(() => !shouldShowPromptAgain());
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    let cancelled = false;

    getOneSignalAppStatus().then((nextStatus) => {
      if (!cancelled) {
        setStatus(nextStatus);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!status || dismissed) {
    return null;
  }

  const active = Boolean(status.optedIn && status.subscriptionId);
  const blocked = status.permissionState === 'denied';
  const needsIosInstall = Boolean(status.needsHomeScreenInstall);

  if (!status.enabled || active || (!status.supported && !blocked && !needsIosInstall)) {
    return null;
  }

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(NEWS_PUSH_PROMPT_DISMISSED_KEY, String(Date.now()));
    }
    setDismissed(true);
  };

  const handleActivate = async () => {
    setUpdating(true);
    setFeedback('');

    try {
      const nextStatus = await requestOneSignalPushPermission();
      setStatus(nextStatus);

      if (nextStatus?.optedIn && nextStatus?.subscriptionId) {
        setFeedback('Pfand-Alarm ist aktiviert.');
      } else if (nextStatus?.permissionState === 'denied') {
        setFeedback('Benachrichtigungen sind im Browser blockiert.');
      } else {
        setFeedback('Pfand-Alarm konnte noch nicht vollstaendig aktiviert werden.');
      }
    } catch (error) {
      setFeedback('Pfand-Alarm konnte nicht aktualisiert werden.');
    } finally {
      setUpdating(false);
    }
  };

  const title = needsIosInstall
    ? 'Push am iPhone vorbereiten'
    : blocked
      ? 'Push ist im Browser blockiert'
      : 'Neuigkeiten als Push erhalten';

  const copy = needsIosInstall
    ? 'Damit Pfandpirat Push senden darf, fuege die App zum Home-Bildschirm hinzu und oeffne sie danach von dort.'
    : blocked
      ? 'Aktiviere Benachrichtigungen in den Browser-Einstellungen, wenn du neue Hinweise direkt bekommen moechtest.'
      : 'Aktiviere den Pfand-Alarm, damit wichtige Neuigkeiten auch als Benachrichtigung bei dir ankommen.';

  return (
    <div className="pfp-news-push-optin">
      <div className="pfp-push-prompt__copy">
        <strong>{title}</strong>
        <span>{copy}</span>
        {feedback && <span className="pfp-news-push-optin__feedback">{feedback}</span>}
      </div>
      <div className="pfp-push-prompt__actions">
        {!blocked && !needsIosInstall && (
          <button
            type="button"
            className="pfp-push-prompt__primary"
            disabled={updating}
            onClick={handleActivate}
          >
            {updating ? 'Aktiviert ...' : 'Push aktivieren'}
          </button>
        )}
        <button type="button" className="pfp-push-prompt__ghost" onClick={handleDismiss}>
          Spaeter
        </button>
      </div>
    </div>
  );
}

export default function AnnouncementCenterPage({ announcements, loading, error, onRead }) {
  return (
    <div className="pfp-news-page">
      <div className="pfp-news-page__header">
        <h2>📢 Neuigkeiten</h2>
        <p>Hier findest du alle aktuellen Hinweise, Updates und wichtigen Änderungen rund um Pfandpirat.</p>
      </div>

      {loading && <div className="pfp-news-card">Neuigkeiten werden geladen …</div>}
      <NewsPushOptInCard />

      {error && <div className="pfp-news-card pfp-news-card--error">{error.message}</div>}

      {!loading && !error && announcements.length === 0 && (
        <div className="pfp-news-card">Aktuell gibt es keine Neuigkeiten.</div>
      )}

      {!loading && !error && announcements.map((item) => (
        <article key={item.id} className={`pfp-news-card pfp-news-card--${item.type || 'info'}`}>
          <div className="pfp-news-card__meta">
            <span className="pfp-news-badge">{getTypeLabel(item.type)}</span>
            <span>{formatDate(item.published_at || item.created_at)}</span>
            {!item.is_read && <span className="pfp-news-unread">neu</span>}
          </div>

          <h3>{item.title}</h3>
          <div
            className="pfp-news-card__body"
            dangerouslySetInnerHTML={{ __html: item.message }}
          />

          <div className="pfp-news-card__actions">
            {!item.is_read && (
              <button type="button" className="pfand-button" onClick={() => onRead(item.id)}>
                Als gelesen markieren
              </button>
            )}

            {item.cta_label && item.cta_url && (
              <a className="pfand-button pfand-button--secondary" href={item.cta_url}>
                {item.cta_label}
              </a>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
